import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { CUSTOM_DATA_SOURCE_PREFIX, DataSource } from "@/domain/energy-source/lib/data-source-quality-mapping";
import { useGetInventoryValue } from "@/domain/inventory/hooks/api/use-get-inventory-value";
import { useUpsertInventoryValueList } from "@/domain/inventory/hooks/api/use-upsert-inventory-value";
import { useCurrentInventoryContext } from "@/domain/inventory/hooks/use-current-inventory-context";
import { convertFactorToStorage } from "@/domain/inventory/lib/energy-and-emissions-conversion";
import { useCurrentSectorContext } from "@/domain/sector/hooks/use-current-sector-context";
import { getSectorDisplayUnit } from "@/domain/sector/lib/get-sector-display-unit";

import { DataSourceSelectDropdown } from "./data-source-select-dropdown";
import DataSourceViewOptional from "./data-source-view-optional";

export const EMISSION_FACTOR_DATA_SOURCE_LIST: DataSource[] = [
  { id: "1", title: "UBA" },
  { id: "2", title: "ifeu" },
  { id: "3", title: "GEMIS" },
  { id: "6", title: "AGFW" },
  { id: "4", title: "Eigene Angabe" },
];

// IPCC AR6 GWP-based factors only apply to direct emission factors that are not
// derived from energy consumption (e.g. the GPC Waste sector); energy-consumption
// factors carry their own sources, so "IPCC AR6" is offered only in those direct
// contexts via this dedicated list.
export const DIRECT_EMISSION_FACTOR_DATA_SOURCE_LIST: DataSource[] = [
  { id: "1", title: "UBA" },
  { id: "2", title: "ifeu" },
  { id: "3", title: "GEMIS" },
  { id: "5", title: "IPCC AR6" },
  { id: "4", title: "Eigene Angabe" },
];

interface RightAsideDataSourceViewProps {
  inventoryValueId: string;
  inventoryValueAmount: number;
  inventoryValueDataSource: string;
  energySourceId?: string;
  sectorId?: string;
  subsectorId?: string;
  subsubsectorId?: string;
  sectorSliceId?: string;
  isReadOnly?: boolean;
}

export const RightAsideDataSourceView: FC<RightAsideDataSourceViewProps> = ({
  inventoryValueId,
  inventoryValueAmount,
  inventoryValueDataSource,
  energySourceId,
  sectorId,
  subsectorId,
  subsubsectorId,
  sectorSliceId,
  isReadOnly = false,
}) => {
  const { t } = useTranslation("inventory");
  const { upsertInventoryValueList } = useUpsertInventoryValueList();
  const { inventoryValue } = useGetInventoryValue({ id: inventoryValueId });
  const { currentInventory } = useCurrentInventoryContext();
  const { currentSector } = useCurrentSectorContext();
  const unit = getSectorDisplayUnit(currentSector);

  const selectedDataSource = useMemo(() => {
    const dataSource = (inventoryValue as { dataSource?: string | null } | null | undefined)?.dataSource;
    if (!dataSource) {
      return undefined;
    }

    if (dataSource.startsWith(CUSTOM_DATA_SOURCE_PREFIX)) {
      return EMISSION_FACTOR_DATA_SOURCE_LIST.find((ds) => ds.title === CUSTOM_DATA_SOURCE_PREFIX);
    }

    return EMISSION_FACTOR_DATA_SOURCE_LIST.find((ds) => ds.title === dataSource);
  }, [inventoryValue]);

  const handleOnDataSourceSelect = (dataSource: DataSource | null) => {
    if (!inventoryValueId) {
      console.warn("Missing inventory value ID");
      return;
    }

    // Get required fields from inventoryValue if it exists, otherwise use props
    const existingValue = inventoryValue as
      | {
          energySourceId?: string;
          sectorId?: string;
          subsectorId?: string;
          subsubsectorId?: string;
          sectorSliceId?: string;
          type?: string;
        }
      | null
      | undefined;
    const finalEnergySourceId = existingValue?.energySourceId || energySourceId;
    const finalSectorId = existingValue?.sectorId || sectorId;
    const finalSubsectorId = existingValue?.subsectorId || subsectorId;
    const finalSubsubsectorId = existingValue?.subsubsectorId || subsubsectorId;
    const finalSectorSliceId = existingValue?.sectorSliceId || sectorSliceId;
    const finalType = existingValue?.type || "Factor";

    if (!dataSource) {
      upsertInventoryValueList({
        upsertInventoryValueInputList: [
          {
            id: inventoryValueId,
            inventoryId: currentInventory.id,
            dataSource: null,
            amount: convertFactorToStorage(inventoryValueAmount, unit),
            type: finalType,
            energySourceId: finalEnergySourceId,
            sectorId: finalSectorId,
            subsectorId: finalSubsectorId,
            subsubsectorId: finalSubsubsectorId,
            sectorSliceId: finalSectorSliceId,
          },
        ],
      });
      return;
    }

    upsertInventoryValueList({
      upsertInventoryValueInputList: [
        {
          id: inventoryValueId,
          inventoryId: currentInventory.id,
          dataSource: dataSource.title,
          amount: convertFactorToStorage(inventoryValueAmount, unit),
          type: finalType,
          energySourceId: finalEnergySourceId,
          sectorId: finalSectorId,
          subsectorId: finalSubsectorId,
          subsubsectorId: finalSubsubsectorId,
          sectorSliceId: finalSectorSliceId,
        },
      ],
    });
  };

  const renderNoInventoryValueText = useCallback(() => {
    if (inventoryValueDataSource) {
      return <div className="ml-3 mt-2 text-xs text-muted-foreground">{inventoryValueDataSource}</div>;
    }
    return <div className="ml-3 mt-2 text-xs italic text-muted-foreground">{t("rightAside.pleaseEnterValue")}</div>;
  }, [inventoryValueDataSource, t]);

  const noInventoryValue = inventoryValueId === "";

  return (
    <div className="flex flex-col">
      <div className="flex ">
        <div className="mt-2 w-24 shrink-0 font-medium">{t("rightAside.source")}</div>
        {noInventoryValue ? (
          renderNoInventoryValueText()
        ) : isReadOnly ? (
          <div className="ml-3 mt-2 text-xs text-muted-foreground">
            <DataSourceViewOptional dataSourceTitle={selectedDataSource?.title} />
          </div>
        ) : (
          <DataSourceSelectDropdown
            onSelect={handleOnDataSourceSelect}
            selectedOption={selectedDataSource ?? null}
            asChild
            isReadOnly={isReadOnly}
          >
            <Button
              variant="ghost"
              size="sm"
              className="overflow-x-hidden font-light ring-offset-background focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
            >
              <DataSourceViewOptional dataSourceTitle={selectedDataSource?.title} />
            </Button>
          </DataSourceSelectDropdown>
        )}
      </div>
      {!noInventoryValue && !selectedDataSource && (
        <div className="mt-2 text-xs italic text-muted-foreground text-red-500">
          {t("rightAside.pleaseAssignSourceToFactor")}
        </div>
      )}
    </div>
  );
};
